import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LoanNoteForm } from "./LoanNoteForm";
import { SOFRChangeForm } from "./SOFRChangeForm";
import { SpreadChangeForm } from "./SpreadChangeForm";
import { Edit, Plus, FileText, Download } from "lucide-react";

interface LoanNoteDetailsProps {
  lenderProfileId: string;
}

export const LoanNoteDetails = ({ lenderProfileId }: LoanNoteDetailsProps) => {
  const { toast } = useToast();
  const [loanNote, setLoanNote] = useState<any>(null);
  const [sofrChanges, setSofrChanges] = useState<any[]>([]);
  const [spreadChanges, setSpreadChanges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLoanNoteDialog, setShowLoanNoteDialog] = useState(false);
  const [showSOFRDialog, setShowSOFRDialog] = useState(false);
  const [showSpreadDialog, setShowSpreadDialog] = useState(false);

  useEffect(() => {
    fetchLoanNote();
  }, [lenderProfileId]);

  const handleDownloadFile = async (fileUrl: string, bucketName: string, documentType: 'loan-agreement' | 'rate-proof' | 'approval-proof') => {
    try {
      // Extract the file path from the public URL
      const urlParts = fileUrl.split(`/storage/v1/object/public/${bucketName}/`);
      if (urlParts.length < 2) {
        toast({
          title: "Error",
          description: "Invalid file URL",
          variant: "destructive",
        });
        return;
      }
      
      const filePath = urlParts[1];
      
      // Download the file
      const { data, error } = await supabase.storage
        .from(bucketName)
        .download(filePath);

      if (error) throw error;

      // Determine file extension from the original file
      const fileExt = filePath.split('.').pop() || 'pdf';
      
      // Create descriptive filename based on document type
      let fileName: string;
      switch (documentType) {
        case 'loan-agreement':
          fileName = `Loan Agreement.${fileExt}`;
          break;
        case 'rate-proof':
          fileName = `Rate Proof.${fileExt}`;
          break;
        case 'approval-proof':
          fileName = `Approval Proof.${fileExt}`;
          break;
        default:
          fileName = `document.${fileExt}`;
      }

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const fetchLoanNote = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("loan_notes")
        .select("*")
        .eq("lender_profile_id", lenderProfileId)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      setLoanNote(data);

      if (data) {
        fetchSOFRChanges(data.id);
        fetchSpreadChanges(data.id);
      }
    } catch (error) {
      console.error("Error fetching loan note:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSOFRChanges = async (loanNoteId: string) => {
    const { data, error } = await supabase
      .from("sofr_changes")
      .select("*")
      .eq("loan_note_id", loanNoteId)
      .order("apply_from_date", { ascending: false });

    if (!error && data) setSofrChanges(data);
  };

  const fetchSpreadChanges = async (loanNoteId: string) => {
    const { data, error } = await supabase
      .from("spread_changes")
      .select("*")
      .eq("loan_note_id", loanNoteId)
      .order("apply_from_date", { ascending: false });

    if (!error && data) setSpreadChanges(data);
  };

  const handleSuccess = () => {
    setShowLoanNoteDialog(false);
    setShowSOFRDialog(false);
    setShowSpreadDialog(false);
    fetchLoanNote();
  };

  if (loading) return <div>Loading...</div>;

  if (!loanNote) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loan Note</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">No loan note created yet</p>
          <Button onClick={() => setShowLoanNoteDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Loan Note
          </Button>
          <Dialog open={showLoanNoteDialog} onOpenChange={setShowLoanNoteDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Loan Note</DialogTitle>
              </DialogHeader>
              <LoanNoteForm
                lenderProfileId={lenderProfileId}
                onSuccess={handleSuccess}
                onCancel={() => setShowLoanNoteDialog(false)}
              />
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Loan Note</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowLoanNoteDialog(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Loan from</p>
              <p className="font-medium">{loanNote.loan_from}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Loan to</p>
              <p className="font-medium">{loanNote.loan_to}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Loan signed on</p>
              <p className="font-medium">{new Date(loanNote.loan_signed_on).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total funds available</p>
              <p className="font-medium">${loanNote.total_funds_available?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Draws allowed</p>
              <p className="font-medium">{loanNote.draws_allowed}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">SOFR on start</p>
              <p className="font-medium">{loanNote.sofr_on_start}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Spread on start</p>
              <p className="font-medium">{loanNote.spread_on_start}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Interest payment schedule</p>
              <p className="font-medium capitalize">{loanNote.interest_payment_schedule}</p>
            </div>
          </div>
          {loanNote.notes && (
            <div>
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="font-medium">{loanNote.notes}</p>
            </div>
          )}
          {loanNote.loan_agreement_url && (
            <div>
              <p className="text-sm text-muted-foreground">Loan Agreement</p>
              <Button
                variant="link"
                className="h-auto p-0 text-primary"
                onClick={() => handleDownloadFile(loanNote.loan_agreement_url!, 'loan-agreements', 'loan-agreement')}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Loan Agreement
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>SOFR Changes</CardTitle>
          <Button size="sm" onClick={() => setShowSOFRDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add SOFR Change
          </Button>
        </CardHeader>
        <CardContent>
          {sofrChanges.length === 0 ? (
            <p className="text-muted-foreground">No SOFR changes recorded</p>
          ) : (
            <div className="space-y-2">
              {sofrChanges.map((change) => (
                <div key={change.id} className="border rounded-lg p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-sm text-muted-foreground">New SOFR</p>
                      <p className="font-medium">{change.new_sofr_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(change.new_sofr_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Apply from</p>
                      <p className="font-medium">{new Date(change.apply_from_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {change.rate_proof_url && (
                    <div className="mt-2 pt-2 border-t">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-primary"
                        onClick={() => handleDownloadFile(change.rate_proof_url!, 'loan-agreements', 'rate-proof')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Rate Proof
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Spread Changes</CardTitle>
          <Button size="sm" onClick={() => setShowSpreadDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Spread Change
          </Button>
        </CardHeader>
        <CardContent>
          {spreadChanges.length === 0 ? (
            <p className="text-muted-foreground">No spread changes recorded</p>
          ) : (
            <div className="space-y-2">
              {spreadChanges.map((change) => (
                <div key={change.id} className="border rounded-lg p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-sm text-muted-foreground">New Spread</p>
                      <p className="font-medium">{change.new_spread_percentage}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{new Date(change.new_spread_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Apply from</p>
                      <p className="font-medium">{new Date(change.apply_from_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {change.change_approval_proof_url && (
                    <div className="mt-2 pt-2 border-t">
                      <Button
                        variant="link"
                        className="h-auto p-0 text-primary"
                        onClick={() => handleDownloadFile(change.change_approval_proof_url!, 'loan-agreements', 'approval-proof')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Approval Proof
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showLoanNoteDialog} onOpenChange={setShowLoanNoteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Loan Note</DialogTitle>
          </DialogHeader>
          <LoanNoteForm
            lenderProfileId={lenderProfileId}
            loanNote={loanNote}
            onSuccess={handleSuccess}
            onCancel={() => setShowLoanNoteDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showSOFRDialog} onOpenChange={setShowSOFRDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record SOFR Change</DialogTitle>
          </DialogHeader>
          <SOFRChangeForm
            loanNoteId={loanNote.id}
            onSuccess={handleSuccess}
            onCancel={() => setShowSOFRDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showSpreadDialog} onOpenChange={setShowSpreadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Spread Change</DialogTitle>
          </DialogHeader>
          <SpreadChangeForm
            loanNoteId={loanNote.id}
            onSuccess={handleSuccess}
            onCancel={() => setShowSpreadDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
